/* eslint-disable max-len */
import React, { useEffect, useState, useContext, Suspense, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Dropdown from '@gcc/pip-components/dist/ThdSelect';
import _filter from 'lodash/filter.js';
import isEmpty from 'lodash/isEmpty.js';
import trimStart from 'lodash/trimStart.js';
import debounce from 'lodash/debounce.js';
import _includes from 'lodash/includes.js';
import { noop } from 'lodash';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { IconButton } from '@one-thd/sui-atomic-components';
import { Close, Search } from '@one-thd/sui-icons';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import MobileSideDrawer from '@gcc/pip-components/dist/MobileSideDrawer';
import { colorSortOptions } from '../../../utils/dropDownOptions';
import { SortButton, PipSortButtonWrapper } from '../../MobileSideDrawerBody/styles';
import LoadMore from '../../loadMore';
import NoAvailableColors from './NoAvailableColors';
import { Container } from '../styles';
import { SwatchObjPropertyPaths } from '../../../common/constants';
import { findTaggedValue } from '../../../utils/tags';
import { ColorFiltersWrapper } from '../../subcomponents/ColorFiltersWrapper';
import BlankLoader from '../../LoadingSpinner/loaderBlank';
import { FilterChips } from '../../FilterChip/filterChip';
import ColorUtils from '../../../utils/color-utils';
import { trackClick } from '../../../utils/analytics';
import { getNumberOfSwatchRows, getSwatchContainerHeight } from './SwatchDisplayUtils';
import ColorSwatches from './ColorSwatches';
import log from '../../../api/logApi';
import { choiceRenderer, valueRenderer } from '../../../utils/render-utils';
import { QUERY_PARAM_SAMPLE_OMSID, Utils } from '../../../utils/urlUtils';
import MobileSideDrawerBody from '../../MobileSideDrawerBody';
import useFilterPanelSticky from '../../../hooks/useFilterPanelSticky';
import useScrollDirection from '../../../hooks/useScrollDirection';

import {
  ColorTitle,
  Wrapper,
  Swatches as SwatchContainer,
  YourSelection,
  ColorRow,
  ColorColumn,
  FiltersAndSwatches,
  SearchInputWrapper,
  SearchInput,
  NoResults,
  SearchError,
  ResetSearch,
  HrSearchTop,
  HrSearchBottom,
  FilterChipsWrapper,
  SuggestionContainer,
  Suggestion,
} from './styles';

const removeSampleColorFromUrl = (history) => {
  if (_includes(history.location.search, QUERY_PARAM_SAMPLE_OMSID)) {
    const splitSearchParams = trimStart(history.location.search, '?')
      .split('&')
      .filter((queryParam) => !_includes(queryParam, QUERY_PARAM_SAMPLE_OMSID));

    // for render-nucleus url structure to still work we don't want to remove all query params
    // example: 123456789?debug=true&experience=exp&expversion=v1
    const sanitizedSearchParams =
      splitSearchParams.length && !isEmpty(splitSearchParams[0])
        ? `?${splitSearchParams.join('&')}`
        : '';

    history.replace(`${history.location.pathname}${sanitizedSearchParams}`);
    return;
  }

  history.replace(`${history.location.pathname}${history.location.search}`);
};

const Colors = (props) => {
  const {
    selectedColorSort,
    setSelectedColor,
    storeNumber,
    isProductAvailable,
    isProductInfoAvailable,
    selectedColorFilters,
    toggleMobileSortDrawer,
    colors,
    sampleOmsId,
    selectedColor,
    history,
    setPrice,
    updateSelectedColorSort,
    updateColorFilters,
    quickViewRef,
    setQuickView,
    onUserHasChangedColor,
    startUpdatePlaConfiguredPrice,
    updateCurrentSku,
    plaColor,
    sideDrawerIsOpen,
    currentOmsId,
    allSurfaceColors,
    selectedThickness,
    surfaces,
    onSetSearchSortChange,
    onSetSearchColorChange,
    onSetSearchTextChange,
    stickyNavRef,
    omsId,
    minimumPromoSqFt,
    isStoreEnabled,
    pipSupportPhoneNumber,
    config,
    colorsRef,
    store,
    useRoomVOIntegration,
    roomVOIntegrationUrl,
  } = props;
  const ctx = useContext(ExperienceContext);
  const { channel } = ctx;
  const isMobile = channel === 'mobile';

  const isConfiguratorPage =  typeof window !== 'undefined' && window.location.href.indexOf('configurator') !== -1;
  const colorRow = useRef();
  const swatchContainer = useRef();
  const desktopStickyFilterPanelRef = useRef(null);
  const [colorQuantity, setColorQuantity] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [swatchContainerHeight, setSwatchContainerHeight] = useState('auto');
  const [ignorePreselectedColor, setIgnorePreselectedColor] = useState(false);
  const [preselectedSampleColor, setPreselectedSampleColor] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [value, setSearchText] = useState('');
  const [filteredSearch, setSearchResultsFiltered] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [sortAnalyticsDispatched, setSortAnalyticsDispatched] = useState(false);
  const [colorAnalyticsDispatched, setColorAnalyticsDispatched] = useState(false);
  const [textAnalyticsDispatched, setTextAnalyticsDispatched] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const nonZeroPricedChoices = _filter(colors, (color) => Number(color.price) > 0);
  let searchResults = colors;
  const [searchSortedChoices, setSortedColors] = useState(null);

  const sortedAndFilteredColors = ColorUtils.getSortedAndFilteredColors(
    colors,
    selectedColorFilters,
    selectedColorSort,
    preselectedSampleColor,
    ignorePreselectedColor,
    isCollapsed,
    surfaces
  );

  const searchTextChanged = useCallback(
    debounce(() => {
      onSetSearchTextChange(
        colors,
        selectedColorFilters,
        selectedColorSort,
        searchString,
        surfaces
      );
      setTextAnalyticsDispatched(true);
    }, 1000),
    [colors, selectedColorFilters, selectedColorSort, searchString, surfaces, onSetSearchTextChange]
  );

  useEffect(() => {
    if (!sortAnalyticsDispatched) {
      onSetSearchSortChange(selectedColorSort);
      setSortAnalyticsDispatched(true);
    }
  }, [selectedColorSort]);

  useEffect(() => {
    if (!colorAnalyticsDispatched) {
      onSetSearchColorChange(
        colors,
        selectedColorFilters,
        selectedColorSort,
        searchString,
        surfaces
      );
      setColorAnalyticsDispatched(true);
    }
  }, [colors, selectedColorSort, searchString]);

  useEffect(() => {
    if (!textAnalyticsDispatched) {
      searchTextChanged(colors, selectedColorFilters, selectedColorSort, searchString, surfaces);
    }
  }, [searchString]);

  const getSearchString = (text) => {
    const activeChoices = ColorUtils?.filterColorsbyFamily(
      nonZeroPricedChoices,
      selectedColorFilters,
      surfaces
    );
    const sortedChoicesArr = ColorUtils?.sortColors(
      activeChoices,
      selectedColorSort,
      SwatchObjPropertyPaths.shortLabel,
      SwatchObjPropertyPaths.mediaUrl
    );

    const substr = text?.currentTarget?.value;
    setSearchString(substr);
    setSearchText(substr);
    setActiveSuggestionIndex(-1);
    setTextAnalyticsDispatched(false);
    const colorsForSuggestions = selectedColorFilters?.length > 0
      ? activeChoices
      : nonZeroPricedChoices;
    const suggestions = ColorUtils.getSearchSuggestions(substr, colorsForSuggestions);
    setSearchSuggestions(suggestions);

    searchResults = ColorUtils.filterColorsBySearchTerm(sortedChoicesArr, substr, 'name');
    const unfilteredResultsArr = ColorUtils.filterColorsBySearchTerm(nonZeroPricedChoices, substr, 'name');
    if (substr === '') {
      setSearchResultsFiltered(null);
    } else {
      setSearchResultsFiltered(unfilteredResultsArr);
    }
    return setSortedColors(searchResults);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchSuggestions([]);
    setActiveSuggestionIndex(-1);
    setSearchText(suggestion);
    setSearchString(suggestion);
    setTextAnalyticsDispatched(false);
    const inputElement = document.getElementById('search-color-field-input');
    if (inputElement) {
      inputElement.value = suggestion;
    }

    const activeChoices = ColorUtils?.filterColorsbyFamily(
      nonZeroPricedChoices,
      selectedColorFilters,
      surfaces
    );
    const sortedChoicesArr = ColorUtils?.sortColors(
      activeChoices,
      selectedColorSort,
      SwatchObjPropertyPaths.shortLabel,
      SwatchObjPropertyPaths.mediaUrl
    );

    const searchResultsArr = ColorUtils.filterColorsBySearchTerm(sortedChoicesArr, suggestion, 'name');
    const unfilteredResults = ColorUtils.filterColorsBySearchTerm(nonZeroPricedChoices, suggestion, 'name');

    setSearchResultsFiltered(unfilteredResults);
    setSortedColors(searchResultsArr);
  };

  const handleKeyDown = (event) => {
    ColorUtils.handleSuggestionKeyDown(
      event,
      searchSuggestions,
      activeSuggestionIndex,
      setActiveSuggestionIndex,
      setSearchSuggestions,
      handleSuggestionClick
    );
  };

  const clearValue = (full) => {
    document.getElementById('search-color-field-input').value = '';
    getSearchString({ currentTarget: { value: '' } });
    setSearchSuggestions([]);
    setActiveSuggestionIndex(-1);
    if (full) {
      updateColorFilters([]);
    }
  };

  const handleUserColorChange = (color, userHasChangedColor) => {
    if (color?.externalId != null) {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.setItem(`userSelectedColorId-${currentOmsId}`, String(color.externalId));
        }
      } catch (error) {
        log('Error saving selected color to sessionStorage', error);
      }
    }
    onUserHasChangedColor(color, userHasChangedColor);
  };

  const searchComponent = (
    <div style={{ padding: '0px 10px 10px 5px' }}>
      <div id="typeahead-container">
        <div className="sui-inline-flex sui-flex-row sui-relative sui-min-w-0 sui-p-0 sui-m-0 sui-align-top sui-w-full-gcc">
          <SearchInputWrapper
            searchString={searchString}
            isMobile={isMobile}
            className="sui-flex sui-text-current sui-ring-0 sui-rounded-base sui-outline-none sui-box-border sui-m-0 sui-block sui-min-w-0 sui-w-full-gcc sui-h-11 sui-pl-3 sui-whitespace-nowrap sui-overflow-hidden sui-border-1 sui-border-solid sui-border-transparent focus-within:sui-border-input-active"
          >
            <SearchInput
              searchString={searchString}
              isMobile={isMobile}
              style={{ width: '100%', backgroundColor: 'rgb(244 244 244 / var(--tw-bg-opacity))' }}
              onChange={(text) => getSearchString(text)}
              onKeyDown={handleKeyDown}
              type="search"
              required=""
              className="focus:placeholder:sui-text-transparent sui-min-w-0 sui-w-full-gcc sui-h-full sui-text-current sui-ring-0 sui-outline-none sui-text-ellipsis sui-appearance-none"
              placeholder="Search Color"
              name="keyword"
              autoComplete="off"
              id="search-color-field-input"
              role="combobox"
              aria-autocomplete="list"
              aria-controls="results-listbox"
              aria-owns="results-listbox"
              aria-expanded={searchSuggestions.length > 0 ? 'true' : 'false'}
            />
            <div className="sui-w-11" style={{ paddingTop: 10, width: '3.4rem', marginRight: 10 }}>
              {searchString === '' ? (
                <IconButton
                  className="sui-px-4"
                  icon={Search}
                  buttonSize="tight"
                  iconSize="small"
                  data-automation-id="exit-button"
                  aria-label="Exit"
                />
              ) : (
                <IconButton
                  className="sui-px-4"
                  onClick={() => clearValue()}
                  icon={Close}
                  buttonSize="tight"
                  iconSize="small"
                  data-automation-id="exit-button"
                  aria-label="Exit"
                />
              )}
            </div>
          </SearchInputWrapper>
          {searchSuggestions.length > 0 && (
            <SuggestionContainer
              id="results-listbox"
              role="listbox"
              className="sui-absolute sui-top-full sui-left-0 sui-right-0 sui-bg-white sui-border sui-border-solid sui-border-gray-300 sui-rounded-base sui-mt-1 sui-max-h-60 sui-overflow-y-auto sui-shadow-lg sui-z-50"
            >
              {searchSuggestions.map((suggestion, index) => (
                <Suggestion
                  key={suggestion}
                  index={index}
                  activeSuggestionIndex={activeSuggestionIndex}
                  role="option"
                  tabIndex={0}
                  aria-selected={index === activeSuggestionIndex}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={() => setActiveSuggestionIndex(index)}
                  className={`sui-px-4 sui-py-3 sui-cursor-pointer hover:sui-bg-gray-100 ${
                    index === activeSuggestionIndex ? 'sui-bg-gray-100' : ''
                  }`}
                >
                  {suggestion}
                </Suggestion>
              ))}
            </SuggestionContainer>
          )}
        </div>
      </div>
    </div>
  );

  const onClickLoadMore = () => {
    trackClick('pagination', {}, 'browse');
    setIsCollapsed(!isCollapsed);

    if (!isCollapsed) {
      window.scroll(0, colorRow.current.offsetTop - stickyNavRef.current.offsetTop);
    }
  };

  const onUpdateColorFilterSort = (choice) => {
    setColorAnalyticsDispatched(false);
    if (choice.length === 0) {
      setIgnorePreselectedColor(false);
    } else {
      setIgnorePreselectedColor(true);
    }
    return updateColorFilters(choice);
  };

  const onUpdateColorSort = (choice) => {
    setSortAnalyticsDispatched(false);
    setIgnorePreselectedColor(true);
    return updateSelectedColorSort(choice.value);
  };

  useEffect(() => {
    updateColorFilters(Utils.getFilters(currentOmsId));
  }, [currentOmsId]);

  useEffect(() => {
    if (!colors || colors.length === 0) {
      return;
    }

    let foundPreselectedSampleColor;

    if (Utils.hasPlaGuid() && plaColor && isInitialLoad) {
      setIsInitialLoad(false);
      if (plaColor.id !== selectedColor.id) {
        const preselectedPlaColor = colors.find((color) => color.id === plaColor.id);
        setPreselectedSampleColor(preselectedPlaColor);
        setSelectedColor(preselectedPlaColor);
      }
    }

    if (sampleOmsId) {
      foundPreselectedSampleColor = colors.find((color) => color.sampleOmsId === sampleOmsId);

      if (foundPreselectedSampleColor) {
        if (preselectedSampleColor?.sampleOmsId !== foundPreselectedSampleColor?.sampleOmsId) {
          setPreselectedSampleColor(foundPreselectedSampleColor);
          setSelectedColor(foundPreselectedSampleColor);
          setPrice(foundPreselectedSampleColor.price);
          removeSampleColorFromUrl(history);
        }
      } else {
        log(`Sample with id ${sampleOmsId} not found`);
        if (!selectedColor) {
          setSelectedColor(ColorUtils.getCheapestColorSwatchWithImage(colors));
        }
        removeSampleColorFromUrl(history);
      }
    } else {
      const previouslySelectedColor = colors.find((color) => color.id === selectedColor.id);

      if (!selectedColor && !previouslySelectedColor) {
        setSelectedColor(ColorUtils.getCheapestColorSwatchWithImage(colors));
        setPrice(0);
      }

      if (isInitialLoad) {
        setIsInitialLoad(false);
        const storedExternalId = sessionStorage.getItem(`userSelectedColorId-${currentOmsId}`);
        const storedColor = storedExternalId
          ? colors.find((color) => String(color.externalId) === storedExternalId)
          : null;
        if (storedColor) {
          if (selectedColor?.id !== storedColor.id) {
            setSelectedColor(storedColor);
            setPrice(storedColor.price);
          }
        } else {
          const rankedColors = colors.filter((color) => ColorUtils.getTopSellerRank(color) >= 1);
          const topSellerColor = rankedColors.reduce((best, current) => {
            if (!best) return current;
            return ColorUtils.getTopSellerRank(current) < ColorUtils.getTopSellerRank(best) ? current : best;
          }, null);
          if (topSellerColor && selectedColor?.id !== topSellerColor.id) {
            setSelectedColor(topSellerColor);
            setPrice(topSellerColor.price);
          }
        }
      }
    }

    if (!preselectedSampleColor && foundPreselectedSampleColor) {
      setPreselectedSampleColor(foundPreselectedSampleColor);
    }
  }, [colors]);

  const swatchRows = getNumberOfSwatchRows(colorQuantity, swatchContainer, colorRow);
  const calculatedSwatchesHeight = getSwatchContainerHeight(
    isCollapsed,
    swatchContainer,
    swatchRows,
    swatchContainerHeight
  );
  const screenWidth =  (typeof window !== 'undefined') ? window.innerWidth : 0;
  const rowLength = screenWidth <= 1024 ? 6 : 10;
  const shouldShowLoadMore =
    searchString?.length >= 1
      ? searchSortedChoices.length > rowLength
      : colors.length > rowLength && sortedAndFilteredColors.length >= rowLength;

  const updateSwatchDisplaySize = () => {
    if (showLoadMore !== shouldShowLoadMore && !searchString?.length >= 1) {
      setShowLoadMore(shouldShowLoadMore);
    }

    if (swatchContainerHeight !== calculatedSwatchesHeight && !searchString?.length >= 1) {
      setSwatchContainerHeight(calculatedSwatchesHeight);
    }
  };

  useEffect(() => {
    updateSwatchDisplaySize();
    if (sortedAndFilteredColors && sortedAndFilteredColors.length > 0) {
      if (colorQuantity !== sortedAndFilteredColors.length) {
        setColorQuantity(sortedAndFilteredColors.length);
      }
    }
  }, [sortedAndFilteredColors, colorQuantity, calculatedSwatchesHeight]);

  useEffect(() => {
    if (searchSortedChoices && searchString?.length >= 1 && !selectedColorFilters.length >= 1) {
      if (searchSortedChoices.length > rowLength) {
        setShowLoadMore(true);
        if (isCollapsed) {
          setSwatchContainerHeight('77rem');
        } else {
          setSwatchContainerHeight('auto');
        }
      }
      if (searchSortedChoices.length <= rowLength) {
        setSwatchContainerHeight('auto');
        setShowLoadMore(false);
      }
    }
    if (selectedColorFilters.length >= 1 && !searchString?.length >= 1) {
      if (isCollapsed) {
        setSwatchContainerHeight('77rem');
      } else {
        setSwatchContainerHeight('auto');
      }
    }
  }, [searchSortedChoices, showLoadMore, isCollapsed]);

  const swatchContainerClasses = classNames({
    swatchContainer: true,
    collapsed: isCollapsed && showLoadMore,
  });

  const combinedColors = () => {
    if (searchString && searchString.length >= 1) {
      const filteredColors = ColorUtils.filterColorsbyFamily(filteredSearch, selectedColorFilters, surfaces);
      return ColorUtils.sortColors(
        filteredColors,
        selectedColorSort,
        SwatchObjPropertyPaths.name,
        SwatchObjPropertyPaths.mediaUrl
      );
    }
    return sortedAndFilteredColors;
  };

  useEffect(() => {
    if (searchSortedChoices && searchString?.length >= 1) {
      const searchSortedFiltercolors = ColorUtils.getSortedAndFilteredColors(
        filteredSearch,
        selectedColorFilters,
        selectedColorSort,
        preselectedSampleColor,
        ignorePreselectedColor,
        surfaces
      );

      if (searchSortedFiltercolors.length <= rowLength) {
        setShowLoadMore(false);
        setSwatchContainerHeight('auto');
      } else {
        setShowLoadMore(true);
        if (isCollapsed) {
          setSwatchContainerHeight('77rem');
        } else {
          setSwatchContainerHeight('auto');
        }
      }
    }
  }, [selectedColorFilters, isCollapsed, searchSortedChoices]);

  const showError = !combinedColors().length > 0;
  // const showError = (hasData && searchString?.length >= 1 && searchSortedChoices?.length === 0);
  const { scrollDirection, prevScrollDirection } = useScrollDirection(220, 20);
  const direction = scrollDirection || prevScrollDirection;
  useFilterPanelSticky({ stickyNavRef, desktopStickyFilterPanelRef, direction });

  return (
    <>
      {!isProductInfoAvailable ? (
        <NoAvailableColors />
      ) : (
        <Container>
          {!isProductAvailable && <div />}
          {isProductAvailable === null && (
            <ColorTitle className="sui-h2-bold sui-line-clamp-unset sui-text-primary">
              Colors
            </ColorTitle>
          )}
          {isProductAvailable && (
            <>
              <ColorTitle className="sui-h2-bold sui-line-clamp-unset sui-text-primary">
                Colors
              </ColorTitle>
              <FiltersAndSwatches>
                {colors.length > 0 && (
                  <div
                    data-element="color-filter"
                    className="ct-color-filter sui-w-72 sui-capitalize sui-pr-4"
                  >
                    <ColorFiltersWrapper
                      surfaces={surfaces}
                      colorChoices={colors}
                      colorChoicesSearch={searchSortedChoices}
                      onUpdateFilter={(choice) => onUpdateColorFilterSort(choice)}
                      selectedFilters={selectedColorFilters}
                      hasSearch={value}
                      filteredSearch={filteredSearch}
                      desktopStickyFilterPanelRef={desktopStickyFilterPanelRef}
                      selectedColorName={selectedColor?.name || selectedColor?.shortLabel}
                      selectedColorImageUrl={selectedColor?.imageUrl || selectedColor?.mediaUrl}
                      history={history}
                      omsId={omsId}
                      isProductAvailable={isProductAvailable}
                      minimumPromoSqFt={minimumPromoSqFt}
                      isStoreEnabled={isStoreEnabled}
                      pipSupportPhoneNumber={pipSupportPhoneNumber}
                      config={config}
                      isProductInfoAvailable={isProductInfoAvailable}
                      colorsRef={colorsRef}
                      selectedColor={selectedColor}
                      store={store}
                      useRoomVOIntegration={useRoomVOIntegration}
                      roomVOIntegrationUrl={roomVOIntegrationUrl}
                    />
                  </div>
                )}
                <ColorRow ref={colorRow} filteredSearch={filteredSearch}>
                  <ColorColumn id="color-column">
                    <PipSortButtonWrapper className="pip-color-sort-wrapper">
                      <YourSelection
                        data-element="YourSelection"
                        filteredSearch={filteredSearch}
                        showError={showError}
                        className="lg:sui-hidden"
                      >
                        <span>Your Selection:</span>
                        <br />
                        <strong>{selectedColor && selectedColor.name}</strong>
                      </YourSelection>
                      <SortButton
                        className="sort-filter-button"
                        onClick={() => toggleMobileSortDrawer()}
                      >
                        Sort & Filter
                      </SortButton>
                    </PipSortButtonWrapper>
                    <div className="pip-color-sort-wrapper">
                      <div className="buybox__super-sku">
                        <Wrapper>
                          {!isMobile && searchComponent}
                          <YourSelection
                            data-element="YourSelection"
                            className="lg:sui-hidden"
                            filteredSearch={filteredSearch}
                            showError={showError}
                          >
                            <span>Your Selection:</span>{' '}
                            <strong>{selectedColor && selectedColor.name}</strong>
                          </YourSelection>
                          <div style={{ padding: '0 1rem', flex: 'auto' }}>
                            <Dropdown
                              options={colorSortOptions}
                              keyBy={(sortBy) => sortBy.label}
                              optionRenderer={choiceRenderer}
                              valueRenderer={valueRenderer}
                              onChange={(choice) => onUpdateColorSort(choice)}
                              value={colorSortOptions?.find((oo) => oo.value === selectedColorSort)}
                            />
                          </div>
                        </Wrapper>
                      </div>
                    </div>
                    {isMobile && searchComponent}
                    {selectedColorFilters?.length >= 1 && isMobile && (
                      <FilterChipsWrapper>
                        <FilterChips
                          colors={colors}
                          selectedColorFilters={selectedColorFilters}
                          surfaces={surfaces}
                          onUpdateFilter={onUpdateColorFilterSort}
                          clearValue={clearValue}
                        />
                      </FilterChipsWrapper>
                    )}
                    {showError ? (
                      <SearchError>
                        <HrSearchTop />
                        <NoResults>
                          {/* eslint-disable-next-line max-len */}
                          {`We couldn't find a match for "${searchString}".`}
                          <br />
                          Try using the Color Family filters, or try another search.
                        </NoResults>
                        <ResetSearch onClick={() => clearValue(true)}>
                          Clear Search & Filters
                        </ResetSearch>
                        <HrSearchBottom />
                      </SearchError>
                    ) : (
                      <SwatchContainer
                        id="swatch-container"
                        ref={swatchContainer}
                        className={swatchContainerClasses}
                        style={{ height: `${swatchContainerHeight}` }}
                        isCollapsed={isCollapsed}
                        isConfiguratorPage={isConfiguratorPage}
                      >
                        <ColorSwatches
                          colors={combinedColors()}
                          storeNumber={storeNumber}
                          setSelectedColor={setSelectedColor}
                          selectedColorId={selectedColor && selectedColor.id}
                          setPrice={setPrice}
                          quickViewRef={quickViewRef}
                          setQuickView={setQuickView}
                          onUserHasChangedColor={handleUserColorChange}
                          startUpdatePlaConfiguredPrice={startUpdatePlaConfiguredPrice}
                          updateCurrentSku={updateCurrentSku}
                          allSurfaceColors={allSurfaceColors}
                          selectedThickness={selectedThickness}
                        />
                      </SwatchContainer>
                    )}
                    {showLoadMore && (
                      <LoadMore
                        onClick={onClickLoadMore}
                        buttonText={isCollapsed ? 'Load More' : 'View Less'}
                      />
                    )}
                  </ColorColumn>
                </ColorRow>
              </FiltersAndSwatches>
              <Suspense fallback={<BlankLoader />}>
                <ErrorBoundary name="mobile-side-drawer">
                  <MobileSideDrawer isOpen={sideDrawerIsOpen}>
                    <MobileSideDrawerBody
                      productId={Number(currentOmsId)}
                      value={value}
                      filteredSearch={filteredSearch}
                      searchSortedChoices={searchSortedChoices}
                      onUpdateColorFilterSort={onUpdateColorFilterSort}
                      colorsRef={colorsRef}
                    />
                  </MobileSideDrawer>
                </ErrorBoundary>
              </Suspense>
            </>
          )}
        </Container>
      )}
    </>
  );
};

Colors.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  colors: PropTypes.array,
  selectedColorSort: PropTypes.string.isRequired,
  updateSelectedColorSort: PropTypes.func.isRequired,
  toggleMobileSortDrawer: PropTypes.func.isRequired,
  onSetSearchSortChange: PropTypes.func.isRequired,
  onSetSearchColorChange: PropTypes.func.isRequired,
  onSetSearchTextChange: PropTypes.func.isRequired,
  setSelectedColor: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedColor: PropTypes.object.isRequired,
  storeNumber: PropTypes.number.isRequired,
  isProductAvailable: PropTypes.bool,
  isProductInfoAvailable: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedColorFilters: PropTypes.array.isRequired,
  updateColorFilters: PropTypes.func.isRequired,
  sampleOmsId: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  history: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  plaColor: PropTypes.object,
  setPrice: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  quickViewRef: PropTypes.object.isRequired,
  setQuickView: PropTypes.func.isRequired,
  onUserHasChangedColor: PropTypes.func,
  startUpdatePlaConfiguredPrice: PropTypes.func,
  updateCurrentSku: PropTypes.func,
  sideDrawerIsOpen: PropTypes.bool,
  currentOmsId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  allSurfaceColors: PropTypes.array.isRequired,
  selectedThickness: PropTypes.number.isRequired,
  surfaces: [
    PropTypes.arrayOf(
      PropTypes.shape({
        shapes: PropTypes.shape({}),
      })
    ),
  ].isRequired,
  stickyNavRef: PropTypes.shape({ current: PropTypes.bool }),
  omsId: PropTypes.number,
  minimumPromoSqFt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isStoreEnabled: PropTypes.bool,
  pipSupportPhoneNumber: PropTypes.string.isRequired,
  config: PropTypes.shape({}),
  colorsRef: PropTypes.shape({}).isRequired,
  store: PropTypes.shape({}),
  useRoomVOIntegration: PropTypes.bool,
  roomVOIntegrationUrl: PropTypes.string,
};

Colors.defaultProps = {
  isProductAvailable: null,
  colors: [],
  sampleOmsId: null,
  onUserHasChangedColor: noop,
  startUpdatePlaConfiguredPrice: noop,
  updateCurrentSku: noop,
  plaColor: null,
  sideDrawerIsOpen: false,
  stickyNavRef: null,
  omsId: null,
  isStoreEnabled: true,
  config: {},
  store: null,
  useRoomVOIntegration: false,
  roomVOIntegrationUrl: null,
};

export default Colors;
